import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { AuthenticateResponse } from 'app/authentitcation/AuthenticateResponse';
import { LoginResponse } from 'app/authentitcation/LoginResponse';
import { Session } from 'app/authentitcation/Session';
import { User } from 'app/authentitcation/User';
import { environment } from 'environments/environment';
import { catchError, map, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor() {}

  private http = inject(HttpClient);
  private USER_LOCAL_STORAGE_KEY = 'currentUser';
  private SESSION_LOCAL_STORAGE_KEY = 'session';

  isAuthenticated = signal(false);

  authenticate() {
    return this.http
      .get<AuthenticateResponse>(`${environment.serverUrl}/user/authenticate`, {
        withCredentials: true,
      })
      .pipe(
        catchError((err) => {
          this.isAuthenticated.set(false);
          return of({ authorities: [] });
        }),
        map((response) => {
          this.isAuthenticated.set(response.authorities.length > 0);
          return this.isAuthenticated();
        })
      );
  }

  /**
   * Logs in the user and saves the user and session data to local storage.
   */
  login(username: string, password: string) {
    return this.http
      .post<LoginResponse>(
        `${environment.serverUrl}/user/login`,
        {},
        {
          withCredentials: true,
          params: {
            username,
            password,
            client: 'Timer',
          },
        }
      )
      .pipe(
        tap((response) => {
          this.saveUserToLocalStorage({
            userId: response.userId,
            username: response.username,
          });
          this.saveSessionToLocalStorage({
            username: response.username,
            presentLogin: response.loginTime.presentLogin,
            lastLogin: response.loginTime.lastLogin,
            sessionTime: response.sessionTime,
          });
        })
      );
  }

  getCurrentUser(): User | null {
    return JSON.parse(localStorage.getItem(this.USER_LOCAL_STORAGE_KEY) ?? 'null');
  }

  private saveUserToLocalStorage(user: User) {
    localStorage.setItem(this.USER_LOCAL_STORAGE_KEY, JSON.stringify(user));
  }

  private saveSessionToLocalStorage(session: Session) {
    localStorage.setItem(this.SESSION_LOCAL_STORAGE_KEY, JSON.stringify(session));
  }

  private getSessionFromLocalStorage(): User | null {
    return JSON.parse(localStorage.getItem(this.SESSION_LOCAL_STORAGE_KEY) || 'null');
  }

  logout() {
    return this.http.get(`${environment.serverUrl}/logout`, { withCredentials: true }).pipe(
      tap(() => {
        localStorage.removeItem(this.SESSION_LOCAL_STORAGE_KEY);
      })
    );
  }
}
